import { AppstoreOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Space, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useTheme } from 'context/ThemeContext';
import Cookies from 'js-cookie';
import { Application } from 'models/Application';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRenderPerformanceMonitor } from 'utils/performanceUtils';

const { Title, Paragraph } = Typography;

// Extended Application interface to handle nested application property
interface ExtendedApplication extends Application {
  application?: {
    accessPermissionName: string;
    description: string;
    id: string;
    logoUrl?: string;
    name: string;
    url: string;
  };
  division?: {
    business: {
      dataAreaId: string;
      erpId: string;
      id: string;
      name: string;
    };
    erpId: string;
    id: string;
    name: string;
  };
  logoUrl?: string;
}

interface ApplicationCardProps {
  application: ExtendedApplication;
}

// App theme colors adapted to match BusinessList.tsx dark theme
const themeColors = {
  primary: '#5867dd',
  info: '#2786fa',
  success: '#18a799',
  error: '#dc3545',
  warning: '#fbaa01',
  normal: '#84859a',
  white: '#fff',
  layoutHeader: '#1f1e2e',
  cardBackground: 'rgba(57, 56, 60, 0.51)',
  cardBorder: 'rgb(69, 68, 76)',
  textColor: '#fff'
};

// Define styles as objects with dark theme
const styles = {
  card: {
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    height: '100%',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid transparent',
    background: themeColors.cardBackground,
    display: 'flex',
    flexDirection: 'column' as const
  },
  cardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${themeColors.cardBorder}`
  },
  cardDisabled: {
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    height: '100%',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid transparent',
    background: themeColors.cardBackground,
    opacity: 0.6,
    filter: 'grayscale(30%)',
    display: 'flex',
    flexDirection: 'column' as const
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    position: 'relative' as const,
    zIndex: 1,
    justifyContent: 'space-between'
    // padding: '16px 8px'
  },
  logoContainer: {
    // marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80px',
    width: '100%'
  },
  avatar: {
    width: '80px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    objectFit: 'contain' as const
  },
  titleContainer: {
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // marginBottom: '12px',
    width: '100%'
  },
  title: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: themeColors.textColor,
    textAlign: 'center' as const,
    lineHeight: 1.2,
    width: '100%'
  },
  descriptionContainer: {
    minHeight: '50px', // Fixed height for 3 lines of text
    // marginBottom: '16px',
    overflow: 'hidden',
    width: '100%',
    display: 'flex'
    // alignItems: 'center',
    // justifyContent: 'center'
  },
  paragraph: {
    color: themeColors.textColor,
    opacity: 0.7,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as any,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center' as const,
    fontSize: '14px',
    lineHeight: '24px',
    margin: 0,
    width: '100%'
  },
  cardFooter: {
    marginTop: 8,
    // paddingTop: '16px',
    // height: '58px', // Fixed height for the footer
    height: '50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  },
  button: {
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.info} 100%)`,
    border: 'none',
    boxShadow: `0 4px 10px rgba(0, 0, 0, 0.25)`,
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  decorationCircle: {
    position: 'absolute' as const,
    borderRadius: '50%',
    opacity: 0.1,
    background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.info} 100%)`,
    transition: 'all 0.3s ease'
  },
  decorationCircle1: {
    top: '-20px',
    right: '-20px',
    width: '100px',
    height: '100px'
  },
  decorationCircle2: {
    bottom: '-30px',
    left: '-30px',
    width: '120px',
    height: '120px'
  },
  badge: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    zIndex: 1000,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: themeColors.textColor,
    borderRadius: '4px',
    padding: '4px 8px'
  }
};

export const ApplicationCard = memo(({ application }: ApplicationCardProps): JSX.Element => {
  // Monitor render performance
  useRenderPerformanceMonitor('ApplicationCard');
  const { isDarkMode } = useTheme();
  const { xs } = useBreakpoint();
  const isMobile = xs;

  const [hovered, setHovered] = useState(false);

  /* ******************** Hooks ******************** */
  const { pathname } = useLocation();

  const isUrlInternal = useMemo(() => pathname.includes('/internal-applications'), [pathname]);

  /* ******************** Variables ******************** */

  const handleClick = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      // Memoize cookie options to prevent recalculation
      const cookieOptions = process.env.REACT_APP_COOKIE_PREFIX === 'localdev' ? { expires: 365 } : { expires: 365, domain: 'mdsiinc.com' };

      Cookies.set(`${process.env.REACT_APP_COOKIE_PREFIX}_internal`, JSON.stringify(isUrlInternal), cookieOptions);

      if (application.url) {
        if (application.name === 'Sales Order Inquiries') {
          window.location.href = `${application.url}/${application.division?.business.dataAreaId}/${application.division?.erpId}`;
        } else {
          window.location.href = application.url;
        }
      }
    },
    [isUrlInternal, application]
  );

  /* ******************** Render ******************** */
  // Determine card style based on access and hover state
  const cardStyle = useMemo(() => {
    const baseStyle = {
      ...styles.card,
      background: isDarkMode ? themeColors.cardBackground : 'rgba(233, 233, 233, 0.78)',
      border: isDarkMode ? `1px solid ${themeColors.cardBorder}` : '1px solid rgb(220, 220, 220)',
      width: '100%', // Make sure card takes full width
      maxWidth: isMobile ? '100%' : '100%' // Limit width on larger screens
    };

    return hovered ? { ...baseStyle, ...styles.cardHover, border: isDarkMode ? `1px solid ${themeColors.cardBorder}` : '1px solid #e8e8e8' } : baseStyle;
  }, [application, hovered, isDarkMode, isMobile]);

  // Get application name, description, and logo URL
  const appName = application.name;
  const appDescription = application.description;
  const appLogo = application.logoUrl;

  return (
    <Card
      hoverable
      style={cardStyle}
      bodyStyle={{
        padding: isMobile ? '12px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        overflow: 'hidden'
      }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div
        style={{
          ...styles.cardContent,
          color: isDarkMode ? themeColors.textColor : '#000'
        }}>
        {/* Decorative elements */}
        <div
          style={{
            transform: hovered ? 'scale(1.1)' : 'scale(1)'
          }}
        />
        <div
          style={{
            transform: hovered ? 'scale(1.1)' : 'scale(1)'
          }}
        />

        <div
          style={{
            ...styles.logoContainer,
            height: isMobile ? '60px' : '80px'
          }}>
          {appLogo ? (
            <img
              src={appLogo}
              alt={`${appName} logo`}
              style={{
                ...styles.avatar,
                transform: hovered ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.3s ease',
                maxWidth: isMobile ? '60px' : '80px',
                maxHeight: isMobile ? '60px' : '80px'
              }}
            />
          ) : (
            <Avatar
              size={isMobile ? 60 : 80}
              style={{
                ...styles.avatar,
                transform: hovered ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.3s ease',
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px'
              }}>
              <AppstoreOutlined style={{ fontSize: isMobile ? 30 : 36, color: themeColors.white }} />
            </Avatar>
          )}
        </div>

        <div
          style={{
            ...styles.titleContainer,
            minHeight: isMobile ? '40px' : '50px'
          }}>
          <Title
            level={4}
            style={{
              ...styles.title,
              color: isDarkMode ? themeColors.textColor : '#000',
              fontSize: isMobile ? '16px' : '18px'
            }}
            ellipsis={{ tooltip: appName }}>
            {appName}
          </Title>
        </div>

        <div
          style={{
            ...styles.descriptionContainer,
            minHeight: isMobile ? '40px' : '50px'
          }}>
          <Paragraph
            style={{
              ...styles.paragraph,
              color: isDarkMode ? themeColors.textColor : '#000',
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: isMobile ? '20px' : '24px'
            }}>
            {appDescription}
          </Paragraph>
        </div>

        <div
          style={{
            ...styles.cardFooter,
            height: isMobile ? '40px' : '50px',
            marginTop: isMobile ? 4 : 8
          }}>
          <Button
            type="primary"
            style={{
              ...styles.button,
              width: '100%',
              transition: 'all 0.3s ease',
              opacity: 1,
              height: isMobile ? '32px' : '38px'
            }}>
            <Space>
              Open App
              <ArrowRightOutlined
                style={{
                  transition: 'transform 0.3s ease',
                  transform: hovered ? 'translateX(4px)' : 'none'
                }}
              />
            </Space>
          </Button>
        </div>
      </div>
    </Card>
  );
});
